/**
 * Catch-all reporting for auth failures that no explicit hook owns.
 *
 * Install this AFTER the specific hooks exist (it is called at the end of main.ts). Wire it up
 * first and it fires for everything, at which point you cannot tell coverage from noise.
 */

import type { App } from 'vue';
import { Hub } from 'aws-amplify/utils';
import { AuthEventReporter } from './AuthEventReporter';
import { isAuthConfigured } from '@/config/auth.config';

export function installAuthErrorBackstops(app: App): void {
  reportMisconfiguration();
  installHubListener();
  installWindowHandlers();
  installVueHandler(app);
}

/**
 * One clear event instead of a class of baffling outages.
 *
 * If the VITE_AWS_* variables drift, Amplify.configure() receives undefineds and every Cognito
 * call fails in a confusing way. isAuthConfigured() already existed in auth.config.ts and was
 * never called by anything.
 */
function reportMisconfiguration(): void {
  try {
    if (isAuthConfigured()) {
      return;
    }
    AuthEventReporter.report({
      stage: 'BOOT_CONFIG',
      step: 'main.bootConfigCheck',
      errorCode: 'AuthMisconfigured',
      errorSource: 'CLIENT',
      // Names only — never the values.
      errorDetail: 'missing or empty VITE_AWS_* configuration at boot',
      wasUserVisible: false,
    });
  } catch {
    /* never block boot */
  }
}

/**
 * Amplify v6's `auth` channel has exactly 7 events, of which only two are failures:
 * tokenRefresh_failure and signInWithRedirect_failure. There is NO signIn_failure in v6 — that
 * was v5, which is what most blog posts still describe.
 *
 * Neither can fire in this app today: SSO is a manual window.location.href assignment rather than
 * signInWithRedirect, and the SPA hands its tokens off and navigates away instead of holding a
 * session long enough to refresh one. So this listener is near-zero value right now — it is here
 * because it costs nothing, cannot duplicate the explicit hooks, and becomes correct the day we
 * adopt signInWithRedirect for SSO.
 */
function installHubListener(): void {
  try {
    Hub.listen('auth', ({ payload }) => {
      if (payload.event !== 'tokenRefresh_failure' && payload.event !== 'signInWithRedirect_failure') {
        return;
      }
      AuthEventReporter.report({
        stage: payload.event === 'tokenRefresh_failure' ? 'TOKEN_REFRESH' : 'SSO_REDIRECT',
        step: `hub.${payload.event}`,
        error: (payload as { data?: { error?: unknown } }).data?.error,
        errorSource: 'COGNITO',
        wasUserVisible: false,
      });
    });
  } catch {
    /* ignore */
  }
}

function installWindowHandlers(): void {
  if (typeof window === 'undefined') {
    return;
  }

  // No handler for this existed anywhere in the app, so an unhandled rejection was invisible.
  window.addEventListener('unhandledrejection', (event) => {
    AuthEventReporter.report({
      stage: 'UNHANDLED_REJECTION',
      step: 'window.unhandledrejection',
      error: event.reason,
      errorSource: 'CLIENT',
      wasUserVisible: false,
    });
  });

  window.addEventListener('error', (event) => {
    // Resource load errors (img/script/link) surface here with no `error` object and are noise.
    if (!event.error) {
      return;
    }
    AuthEventReporter.report({
      stage: 'UNCAUGHT_ERROR',
      step: 'window.error',
      error: event.error,
      errorSource: 'CLIENT',
      wasUserVisible: false,
    });
  });
}

/**
 * CHAINS onto any existing handler rather than replacing it — Sentry's Vue integration installs
 * its own app.config.errorHandler, and overwriting it would silently disable Sentry's Vue
 * error capture.
 */
function installVueHandler(app: App): void {
  const previous = app.config.errorHandler;

  app.config.errorHandler = (err, instance, info) => {
    AuthEventReporter.report({
      stage: 'UNCAUGHT_ERROR',
      step: 'vue.errorHandler',
      error: err,
      errorSource: 'CLIENT',
      errorDetail: `vue lifecycle: ${info}`,
      wasUserVisible: false,
    });

    if (typeof previous === 'function') {
      previous(err, instance, info);
    } else {
      console.error('[vue] uncaught error', err);
    }
  };
}
